/**
 * AP-29-E / AP-29-N2 — Was ein Mandant hat, was er zahlt, und wie man es ändert.
 *
 * Bewusst **keine** Listen-Standard-Seite: Das hier ist kein Datenbestand, den
 * man filtert und exportiert, sondern ein fester Katalog — eine Karte je Modul,
 * darin seine Bausteine. Eine FilterBar darüber wäre Zierrat.
 *
 * Drei Dinge tragen den Aufbau:
 *
 * 1. **Gruppiert wird nach Modul, nicht nach Baustein.** Die Plätze gehören dem
 *    Modul (AP-29-N): Sechs Bausteinzeilen zeigten sechsmal dieselbe Platzzahl,
 *    und wer sie an der falschen Zeile ändert, glaubt, er habe nur den einen
 *    Baustein angefasst. Deshalb **ein** Platzblock je Karte.
 * 2. **Geschaltet wird über die Zielmenge**, nicht über Einzelklicks. Der
 *    Betreiber hakt an, was der Betrieb danach haben soll; die Differenz bildet
 *    der Server in **einer** Transaktion (`PUT /admin/tenants/:id/modules`), mit
 *    Vorschau. Ein Wechsel von „Projektzeit" auf „komplett" war vorher vier
 *    Klicks, von denen einer vergessen wird.
 * 3. **Gesprochen wird in Vertriebsbezeichnungen.** `TIME_TRACKING.SCAN` ist ein
 *    Schlüssel, kein Produktname; er steht klein daneben, damit er beim
 *    Nachschlagen auffindbar bleibt.
 *
 * Die Arbeitsteilung bei den Plätzen steht auch auf der Seite: Der **Betreiber**
 * verkauft die Zahl, der **Mandant** verteilt sie namentlich (AP-29-H).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, AlertTriangle, Users, Package, Euro, Check, Trash2, Info, Mail, Bot, HardDrive, Cloud } from 'lucide-react'
import { MAIL_EXTRA_QUOTA_MAX, MODULE_REGISTRY, groupOf, isBookable, geldMitWaehrung } from '@symphenia/shared-types'
import { formCard, pageWide } from '@/lib/form-styles'
import MandantenendeReiter from '../components/MandantenendeReiter'
import { moduleLabel } from '@/lib/module-label'
import { speicherProzent, speicherText } from '@/lib/speicher'
import { FeldStatusZeichen, feldKlasse, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import {
  operatorApi,
  type OperatorTenantModules,
  type OperatorModuleRow,
  type ModulePackage,
  type CatalogRow,
  type TenantPriceRow,
  type SetModulesResult,
  type SeatMode,
  type OperatorMailQuota,
  type OperatorAiQuota,
  type OperatorStorage,
  type OperatorOwnStorage,
} from '../api/operator.api'

const STATUS_LABEL: Record<string, string> = {
  TRIAL: 'Testphase',
  ACTIVE: 'Gebucht',
  EXPIRED: 'Abgelaufen',
  CANCELLED: 'Gekündigt',
  SUSPENDED: 'Ausgesetzt',
}

const STATUS_CLASS: Record<string, string> = {
  TRIAL: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  EXPIRED: 'bg-muted text-muted-foreground',
  CANCELLED: 'bg-muted text-muted-foreground',
  SUSPENDED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
}

/** Läuft diese Buchung gerade? Nur was läuft, steht in der Zielmenge. */
function laeuft(zeile: OperatorModuleRow): boolean {
  const s = zeile.booking?.status
  return s === 'ACTIVE' || s === 'TRIAL'
}

function datum(wert: string | null | undefined): string {
  return wert ? new Date(wert).toLocaleDateString() : '—'
}

function betrag(wert: number | null | undefined, waehrung = 'EUR'): string {
  return geldMitWaehrung(wert, waehrung) || '—'
}

const BEZUG_KURZ: Record<string, string> = { PER_SEAT: 'je Platz', FLAT: 'pauschal' }
const PERIODE_KURZ: Record<string, string> = { MONTH: 'Monat', YEAR: 'Jahr' }

function preisText(p: { amount: number; currency: string; basis: string | null; period: string | null }): string {
  const teile = [betrag(p.amount, p.currency)]
  if (p.basis) teile.push(BEZUG_KURZ[p.basis] ?? p.basis)
  if (p.period) teile.push(`/ ${PERIODE_KURZ[p.period] ?? p.period}`)
  return teile.join(' ')
}

function meldung(e: unknown, ersatz: string): string {
  const antwort = (e as { response?: { data?: { message?: string | string[] } } })?.response?.data
    ?.message
  return Array.isArray(antwort) ? antwort.join(' · ') : (antwort ?? ersatz)
}

/** Eine Modulgruppe mit ihren buchbaren Bausteinen. */
interface Gruppe {
  key: string
  /** Die Zeile der Gruppe selbst — bei einem eigenständigen Modul dieselbe wie ihr einziger Baustein. */
  kopf: OperatorModuleRow | undefined
  teile: OperatorModuleRow[]
}

export default function OperatorTenantModulesPage() {
  const { tenantId = '' } = useParams<{ tenantId: string }>()
  const { t } = useTranslation()

  const [stand, setStand] = useState<OperatorTenantModules | null>(null)
  const [pakete, setPakete] = useState<ModulePackage[]>([])
  const [katalog, setKatalog] = useState<CatalogRow[]>([])
  const [preise, setPreise] = useState<TenantPriceRow[]>([])
  const [reiter, setReiter] = useState<'module' | 'preise' | 'ende'>('module')
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuftGerade, setLaeuftGerade] = useState<string | null>(null)

  /** Die Zielmenge — was der Mandant nach dem Übernehmen haben soll. */
  const [ziel, setZiel] = useState<Set<string>>(new Set())
  const [herkunft, setHerkunft] = useState<string>('')
  const [testEnde, setTestEnde] = useState('')
  const [laufzeitEnde, setLaufzeitEnde] = useState('')
  const [vorschau, setVorschau] = useState<SetModulesResult | null>(null)

  const salesNames = useMemo(
    () => Object.fromEntries(katalog.map((k) => [k.itemKey, k.salesName])),
    [katalog],
  )
  const name = useCallback((key: string) => moduleLabel(key, t, salesNames), [t, salesNames])

  const laden = useCallback(async () => {
    setLoading(true)
    setFehler(null)
    try {
      const daten = await operatorApi.getTenantModules(tenantId)
      setStand(daten)
      // Die Zielmenge startet auf dem Ist-Stand: Wer nichts anfasst, ändert
      // nichts — und ein versehentliches Übernehmen kündigt nichts ab.
      setZiel(new Set(daten.modules.filter(laeuft).map((m) => m.key)))
      setVorschau(null)
    } catch (e) {
      setFehler('Der Modulstand konnte nicht geladen werden.')
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    void laden()
  }, [laden])

  useEffect(() => {
    // Fehler an den Nebensachen dürfen den Modulstand nicht mitnehmen: Paket,
    // Bezeichnung und Preis sind Beiwerk der Maske, nicht ihr Gegenstand.
    operatorApi.listPackages().then(setPakete).catch(() => setPakete([]))
    operatorApi.listCatalog().then(setKatalog).catch(() => setKatalog([]))
    operatorApi.listTenantPrices(tenantId).then(setPreise).catch(() => setPreise([]))
  }, [tenantId])

  /**
   * Jede Schaltung geht denselben Weg: ausführen, die Meldung des Servers
   * unverändert zeigen, neu laden. Die Meldungen sind dort bewusst konkret
   * („Der Mandant muss zuerst 2 Zuteilungen zurücknehmen") — sie hier zu
   * ersetzen würde die Auskunft verschlechtern.
   */
  const schalten = useCallback(
    async (kenner: string, tun: () => Promise<unknown>) => {
      setLaeuftGerade(kenner)
      setFehler(null)
      try {
        await tun()
        await laden()
        setPreise(await operatorApi.listTenantPrices(tenantId))
      } catch (e: unknown) {
        setFehler(meldung(e, 'Die Schaltung ist fehlgeschlagen.'))
      } finally {
        setLaeuftGerade(null)
      }
    },
    [laden, tenantId],
  )

  const gruppen = useMemo<Gruppe[]>(() => {
    if (!stand) return []
    const nach = new Map<string, Gruppe>()
    for (const zeile of stand.modules) {
      const g = zeile.parent ?? groupOf(zeile.key) ?? zeile.key
      if (!nach.has(g)) nach.set(g, { key: g, kopf: undefined, teile: [] })
      const eintrag = nach.get(g)!
      if (zeile.key === g) eintrag.kopf = zeile
      if (zeile.bookable) eintrag.teile.push(zeile)
    }
    // Gruppen ohne einen einzigen buchbaren Baustein sind nichts, was der
    // Betreiber schalten könnte — sie gehören in die Registry, nicht hierher.
    return [...nach.values()].filter((g) => g.teile.length > 0)
  }, [stand])

  const umschalten = (key: string) => {
    setVorschau(null)
    setZiel((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const istStand = useMemo(
    () => new Set<string>((stand?.modules ?? []).filter(laeuft).map((m) => m.key)),
    [stand],
  )
  const geaendert = useMemo(
    () => ziel.size !== istStand.size || [...ziel].some((k) => !istStand.has(k)),
    [ziel, istStand],
  )

  const umbuchen = async (nurRechnen: boolean) => {
    setLaeuftGerade('__ziel__')
    setFehler(null)
    try {
      const ergebnis = await operatorApi.setModules(tenantId, {
        moduleKeys: [...ziel],
        ...(herkunft ? { packageKey: herkunft } : {}),
        ...(testEnde ? { trialEndsAt: new Date(testEnde).toISOString() } : {}),
        ...(laufzeitEnde ? { validUntil: new Date(laufzeitEnde).toISOString() } : {}),
        dryRun: nurRechnen,
      })
      setVorschau(ergebnis)
      if (!nurRechnen) {
        await laden()
        setPreise(await operatorApi.listTenantPrices(tenantId))
      }
    } catch (e) {
      setFehler(meldung(e, 'Das Umbuchen ist fehlgeschlagen.'))
    } finally {
      setLaeuftGerade(null)
    }
  }

  if (loading && !stand) {
    return (
      <div className={pageWide}>
        <p className="text-muted-foreground">Laden…</p>
      </div>
    )
  }

  if (!stand) {
    return (
      <div className={pageWide}>
        <p className="text-destructive">{fehler ?? 'Mandant nicht gefunden.'}</p>
        <Link to="/admin/mandanten" className="text-sm text-primary hover:underline">
          Zurück zur Liste
        </Link>
      </div>
    )
  }

  const aktiveBenutzer = stand.tenant.activeUsers

  return (
    <div className={pageWide}>
      <div>
        <Link
          to="/admin/mandanten"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} /> Mandanten
        </Link>
        <h1 className="text-3xl font-bold text-foreground">{stand.tenant.name}</h1>
        <p className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span className="font-mono text-xs">{stand.tenant.subdomain}</span>
          <span className="flex items-center gap-1">
            <Users size={13} /> {aktiveBenutzer} aktive Benutzer
          </span>
        </p>
      </div>

      <div className="flex gap-1 border-b border-border">
        {(
          [
            ['module', 'Module & Bausteine'],
            ['preise', 'Preise dieses Mandanten'],
            ['ende', 'Mandantenende'],
          ] as const
        ).map(([wert, beschriftung]) => (
          <button
            key={wert}
            type="button"
            onClick={() => setReiter(wert)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              reiter === wert
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {beschriftung}
          </button>
        ))}
      </div>

      {fehler && (
        <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>
      )}

      {reiter === 'module' ? (
        <>
          <ZielLeiste
            pakete={pakete}
            herkunft={herkunft}
            setHerkunft={setHerkunft}
            testEnde={testEnde}
            setTestEnde={setTestEnde}
            laufzeitEnde={laufzeitEnde}
            setLaufzeitEnde={setLaufzeitEnde}
            geaendert={geaendert}
            beschaeftigt={laeuftGerade === '__ziel__'}
            vorschau={vorschau}
            name={name}
            onPaket={(paket) => {
              setVorschau(null)
              setHerkunft(paket.key)
              // Ergänzen statt ersetzen: Ein Paket ist eine Vorauswahl, keine
              // Kündigung dessen, was der Betrieb schon hat.
              setZiel((prev) => new Set([...prev, ...paket.resolvedModuleKeys]))
            }}
            onVorschau={() => umbuchen(true)}
            onUebernehmen={() => umbuchen(false)}
            onZuruecksetzen={() => {
              setZiel(new Set(istStand))
              setHerkunft('')
              setVorschau(null)
            }}
          />

          <MailKontingentBlock tenantId={tenantId} />
          <KiKontingentBlock tenantId={tenantId} />
          <SpeicherKontingentBlock tenantId={tenantId} />
          <EigenerSpeicherBlock tenantId={tenantId} />

          <div className="space-y-4">
            {gruppen.map((gruppe) => (
              <GruppenKarte
                key={gruppe.key}
                gruppe={gruppe}
                name={name}
                aktiveBenutzer={aktiveBenutzer}
                ziel={ziel}
                onUmschalten={umschalten}
                beschaeftigt={laeuftGerade === gruppe.key}
                onPlaetze={(payload) => {
                  // Die Plätze liegen im Pool der Gruppe; geschaltet wird über
                  // irgendeinen gebuchten Baustein — sie treffen denselben Topf.
                  const anker = gruppe.teile.find((tl) => tl.booking)?.key
                  if (!anker) return
                  void schalten(gruppe.key, () =>
                    operatorApi.updateBooking(tenantId, anker, payload),
                  )
                }}
                onStatus={(moduleKey, status) =>
                  void schalten(gruppe.key, () =>
                    operatorApi.updateBooking(tenantId, moduleKey, { status }),
                  )
                }
              />
            ))}
          </div>
        </>
      ) : reiter === 'ende' ? (
        <MandantenendeReiter tenantId={tenantId} />
      ) : (
        <PreisTabelle
          preise={preise}
          pakete={pakete}
          stand={stand}
          name={name}
          beschaeftigt={laeuftGerade}
          onSetzen={(itemKey, payload) =>
            schalten(`preis:${itemKey}`, () => operatorApi.setTenantPrice(tenantId, itemKey, payload))
          }
          onEntfernen={(itemKey, validFrom) =>
            schalten(`preis:${itemKey}`, () =>
              operatorApi.removeTenantPrice(tenantId, itemKey, validFrom),
            )
          }
          onBuchungspreis={(moduleKey, amount, basis, period) =>
            schalten(`preis:${moduleKey}`, () =>
              operatorApi.updateBooking(tenantId, moduleKey, {
                priceAmount: amount,
                priceBasis: basis,
                pricePeriod: period,
              }),
            )
          }
        />
      )}
    </div>
  )
}

// ─── Mail-Kontingent (AP-10 Nachtrag 3) ───────────────────────────────────────

/**
 * Verbrauch dieses Monats und die **Zusatzmenge**, frei eingetragen. Die Grenze
 * ist Grundkontingent plus Zusatz; sie wirkt beim nächsten Versand. Eigener
 * Ladeweg: Ein Fehler hier darf den Modulstand nicht mitnehmen.
 */
function MailKontingentBlock({ tenantId }: { tenantId: string }) {
  const [stand, setStand] = useState<OperatorMailQuota | null>(null)
  const [zusatz, setZusatz] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [gespeichert, setGespeichert] = useState(false)

  useEffect(() => {
    operatorApi
      .getMailQuota(tenantId)
      .then((s) => {
        setStand(s)
        setZusatz(String(s.extra))
      })
      .catch((e) => setFehler(meldung(e, 'Das Mail-Kontingent konnte nicht geladen werden.')))
  }, [tenantId])

  if (!stand) return fehler ? <p className="text-sm text-destructive">{fehler}</p> : null

  const zahl = (n: number) => new Intl.NumberFormat('de-AT').format(n)
  const n = Number(zusatz)
  const gueltig = Number.isInteger(n) && n >= 0 && n <= MAIL_EXTRA_QUOTA_MAX
  const geaendert = gueltig && n !== stand.extra

  const speichern = async () => {
    setLaeuft(true)
    setFehler(null)
    setGespeichert(false)
    try {
      const neu = await operatorApi.setMailQuota(tenantId, n)
      setStand(neu)
      setZusatz(String(neu.extra))
      setGespeichert(true)
    } catch (e) {
      setFehler(meldung(e, 'Die Zusatzmenge konnte nicht gespeichert werden.'))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className={formCard}>
      <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
        <Mail size={16} /> Mail-Kontingent
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {stand.month}: {zahl(stand.used)} von {zahl(stand.limit)} Mails versendet · Grundkontingent{' '}
        {zahl(stand.base)} + Zusatz {zahl(stand.extra)}. Systempost zählt nicht.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="space-y-1">
          <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Zusätzliche Mails pro Monat
          </span>
          <input
            type="number"
            min={0}
            max={MAIL_EXTRA_QUOTA_MAX}
            step={1}
            value={zusatz}
            onChange={(e) => {
              setGespeichert(false)
              setZusatz(e.target.value)
            }}
            className="w-44 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>
        <button
          type="button"
          disabled={!geaendert || laeuft}
          onClick={() => void speichern()}
          className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {laeuft ? 'Speichern…' : 'Speichern'}
        </button>
        {gueltig && <span className="text-sm text-muted-foreground">neue Grenze: {zahl(stand.base + n)}</span>}
        {gespeichert && <span className="text-sm text-emerald-600">✓ gespeichert</span>}
      </div>
      {!gueltig && <p className="mt-2 text-xs text-destructive">Ganze Zahl zwischen 0 und {zahl(MAIL_EXTRA_QUOTA_MAX)}.</p>}
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
    </div>
  )
}

// ─── Symphenia-KI-Kontingent (30.9.2026) ──────────────────────────────────────

/**
 * **Symphenia-Monatslimit je Mandant** in Tokens — für Mandanten ohne eigene KI-Anbindung.
 * Es gilt für jeden KI-Aufruf über den Schlüssel von Symphenia (heute die Kalkulation);
 * ein eigener Schlüssel des Mandanten ist ohne Grenze. 0 heißt „kein Kontingent".
 * Speichert beim Verlassen des Felds (AP-36). Eigener Ladeweg wie das Mail-Kontingent.
 */
function KiKontingentBlock({ tenantId }: { tenantId: string }) {
  const [stand, setStand] = useState<OperatorAiQuota | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    operatorApi
      .getAiQuota(tenantId)
      .then(setStand)
      .catch((e) => setFehler(meldung(e, 'Das KI-Kontingent konnte nicht geladen werden.')))
  }, [tenantId])

  if (!stand) return fehler ? <p className="text-sm text-destructive">{fehler}</p> : null
  return <KiKontingentKarte tenantId={tenantId} stand={stand} onStand={setStand} />
}

function KiKontingentKarte({ tenantId, stand, onStand }: { tenantId: string; stand: OperatorAiQuota; onStand: (s: OperatorAiQuota) => void }) {
  const daten = useMemo(() => ({ monthlyLimit: stand.limit }) as unknown as Datensatz, [stand.limit])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `ai-quota:${tenantId}`,
    bezeichnung: 'Symphenia-KI-Kontingent',
    daten,
    schreibe: (patch) =>
      operatorApi.setAiQuota(tenantId, Number(patch.monthlyLimit ?? 0)).then((neu) => {
        onStand(neu)
        return { data: { monthlyLimit: neu.limit } } as unknown as Datensatz
      }),
  }) as unknown as Speicherstelle<unknown>
  const f = useFeld<number | null>(stelle, 'monthlyLimit', {
    art: 'zahl',
    label: 'Monatslimit',
    stellen: 0,
    pflicht: true,
    pruefe: (w) => (w !== null && (w < 0 || w > 1_000_000_000) ? 'Ganze Zahl zwischen 0 und 1.000.000.000.' : null),
  })
  const zahl = (n: number) => new Intl.NumberFormat('de-DE').format(n)
  const prozent = stand.limit > 0 ? Math.min(100, Math.round((stand.used / stand.limit) * 100)) : 100

  return (
    <div className={formCard}>
      <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
        <Bot size={16} /> Symphenia-KI-Kontingent
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Für Mandanten ohne eigene KI-Anbindung: so viele Tokens je Monat darf der Betrieb über den Schlüssel von
        Symphenia verbrauchen. 0 = kein Kontingent.
      </p>
      {stand.ownKey && (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400">
          <Check size={14} /> Eigener KI-Schlüssel hinterlegt — dafür gilt keine Grenze.
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-end gap-4">
        <label className="space-y-1">
          <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Monatslimit (Tokens)</span>
          <span className="flex items-center gap-1.5">
            <input className={`${feldKlasse} w-44 text-right`} inputMode="numeric" {...f.props} />
            <FeldStatusZeichen status={f} />
          </span>
        </label>
        <div className="min-w-[14rem] flex-1 space-y-1 text-sm text-muted-foreground">
          <div>
            {stand.month}: {zahl(stand.used)} von {stand.limit > 0 ? zahl(stand.limit) : '0'} Tokens verbraucht
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div className={`h-full ${prozent >= 100 ? 'bg-destructive' : prozent >= 80 ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${prozent}%` }} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Speicher-Kontingent (AP-11-E Phase E4) ───────────────────────────────────

const GB = 1024 * 1024 * 1024
/** Dieselbe Obergrenze wie `SetStorageQuotaDto` im Backend. */
const SPEICHER_KONTINGENT_MAX_GB = 10_000

/**
 * **Speicher je Mandant**: belegt, Kontingent, und das Feld, über das ein
 * Zusatzpaket oder ein Enterprise-Vertrag eingetragen wird. Leer heißt: Es gilt
 * der Startwert des Plans. Speichert beim Verlassen des Felds (AP-36).
 */
function SpeicherKontingentBlock({ tenantId }: { tenantId: string }) {
  const [stand, setStand] = useState<OperatorStorage | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    operatorApi
      .getStorage(tenantId)
      .then(setStand)
      .catch((e) => setFehler(meldung(e, 'Der Speicher konnte nicht geladen werden.')))
  }, [tenantId])

  if (!stand) return fehler ? <p className="text-sm text-destructive">{fehler}</p> : null
  return <SpeicherKontingentKarte tenantId={tenantId} stand={stand} onStand={setStand} />
}

function SpeicherKontingentKarte({ tenantId, stand, onStand }: { tenantId: string; stand: OperatorStorage; onStand: (s: OperatorStorage) => void }) {
  const eigen = stand.limitSource === 'CUSTOM' && stand.limitBytes !== null ? Math.round(stand.limitBytes / GB) : null
  const daten = useMemo(() => ({ limitGb: eigen }) as unknown as Datensatz, [eigen])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `storage-quota:${tenantId}`,
    bezeichnung: 'Speicher-Kontingent',
    daten,
    schreibe: (patch) =>
      operatorApi.setStorageQuota(tenantId, (patch.limitGb ?? null) as number | null).then((neu) => {
        onStand(neu)
        const wert = neu.limitSource === 'CUSTOM' && neu.limitBytes !== null ? Math.round(neu.limitBytes / GB) : null
        return { data: { limitGb: wert } } as unknown as Datensatz
      }),
  }) as unknown as Speicherstelle<unknown>
  const f = useFeld<number | null>(stelle, 'limitGb', {
    art: 'zahl',
    label: 'Kontingent',
    stellen: 0,
    pruefe: (w) =>
      w !== null && (!Number.isInteger(w) || w < 1 || w > SPEICHER_KONTINGENT_MAX_GB)
        ? `Ganze Zahl zwischen 1 und ${new Intl.NumberFormat('de-DE').format(SPEICHER_KONTINGENT_MAX_GB)} — oder leer für den Startwert des Plans.`
        : null,
  })
  const prozent = speicherProzent(stand)
  const plan = stand.planLimitBytes === null ? 'ohne Grenze' : speicherText(stand.planLimitBytes)

  return (
    <div className={formCard}>
      <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
        <HardDrive size={16} /> Speicher-Kontingent
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        So viel Platz darf der Betrieb im Speicher von Symphenia belegen. Leer = Startwert des Plans ({plan}). Ein
        Zusatzpaket oder einen Enterprise-Vertrag trägst du hier als Gesamtwert ein.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-4">
        <label className="space-y-1">
          <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Kontingent (GB)</span>
          <span className="flex items-center gap-1.5">
            <input className={`${feldKlasse} w-32 text-right`} inputMode="numeric" placeholder="Plan" {...f.props} />
            <FeldStatusZeichen status={f} />
          </span>
        </label>
        <div className="min-w-[14rem] flex-1 space-y-1 text-sm text-muted-foreground">
          <div>
            {stand.limitBytes === null
              ? `${speicherText(stand.usedBytes)} belegt — ${stand.limitSource === 'OWN' ? 'eigener Speicher, dort gilt kein Kontingent' : 'ohne Grenze'}`
              : `${speicherText(stand.usedBytes)} von ${speicherText(stand.limitBytes)} belegt (${prozent} %)`}
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full ${stand.level === 'FULL' ? 'bg-destructive' : stand.level === 'WARN' ? 'bg-amber-500' : 'bg-primary'}`}
              style={{ width: `${stand.limitBytes === null ? 0 : prozent}%` }}
            />
          </div>
        </div>
      </div>
      {!stand.storageReady && (
        <p className="mt-2 text-sm text-destructive">Der zentrale Dateispeicher ist nicht eingerichtet — Uploads werden abgewiesen.</p>
      )}
    </div>
  )
}

// ─── Eigener Speicher (AP-11-E Phase E5) ──────────────────────────────────────

/**
 * **Eigener Speicher des Mandanten**: wohin er verbunden ist — ohne Zugangsdaten,
 * die sieht auch der Betreiber nie —, wie der Umzug steht, und der eine Handgriff,
 * der hier liegt: den **Rückweg** auf den Symphenia-Speicher freigeben. Ohne diese
 * Zustimmung kann der Mandant nicht zurückwechseln (Festlegung 3).
 *
 * Erscheint nur, wenn es etwas zu zeigen gibt: verbundener Speicher oder ein
 * Umzug, der noch nicht durch ist.
 */
function EigenerSpeicherBlock({ tenantId }: { tenantId: string }) {
  const [stand, setStand] = useState<OperatorOwnStorage | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [beschaeftigt, setBeschaeftigt] = useState(false)

  useEffect(() => {
    operatorApi
      .getOwnStorage(tenantId)
      .then(setStand)
      .catch((e) => setFehler(meldung(e, 'Der eigene Speicher konnte nicht geladen werden.')))
  }, [tenantId])

  if (!stand) return fehler ? <p className="text-sm text-destructive">{fehler}</p> : null
  const umzugOffen = !!stand.move && stand.move.status !== 'DONE'
  if (stand.provider === 'central' && !umzugOffen) return null

  const schalte = async (approved: boolean) => {
    setBeschaeftigt(true)
    setFehler(null)
    try {
      setStand(await operatorApi.setOwnStorageReturn(tenantId, approved))
    } catch (e) {
      setFehler(meldung(e, 'Die Freigabe konnte nicht geändert werden.'))
    } finally {
      setBeschaeftigt(false)
    }
  }
  const zahl = (n: number) => new Intl.NumberFormat('de-DE').format(n)
  const datum = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })

  return (
    <div className={formCard}>
      <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
        <Cloud size={16} /> Eigener Speicher
      </h2>
      {stand.provider === 's3' ? (
        <>
          <p className="mt-1 text-sm text-muted-foreground">
            Die Dateien dieses Betriebs liegen in seinem eigenen Speicher. Für Verfügbarkeit und Sicherung ist der Kunde
            zuständig; ein Kontingent gilt dort nicht. Die Zugangsdaten sind hier nicht einsehbar.
          </p>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
            <dt className="text-muted-foreground">Bucket</dt>
            <dd className="font-medium text-foreground">{stand.bucket}</dd>
            <dt className="text-muted-foreground">Speicher</dt>
            <dd className="text-foreground">{stand.endpoint ?? `Amazon S3, Region ${stand.region ?? ''}`}</dd>
            <dt className="text-muted-foreground">Zugriffsschlüssel</dt>
            <dd className="text-foreground">…{stand.accessKeySuffix}</dd>
            {stand.connectedAt && (
              <>
                <dt className="text-muted-foreground">Verbunden seit</dt>
                <dd className="text-foreground">{datum(stand.connectedAt)}</dd>
              </>
            )}
          </dl>
        </>
      ) : (
        <p className="mt-1 text-sm text-muted-foreground">
          Der Betrieb wechselt zurück auf den Speicher von Symphenia; die Dateien ziehen gerade um.
        </p>
      )}

      {stand.move && umzugOffen && (
        <p className={`mt-3 text-sm ${stand.move.status === 'FAILED' ? 'text-destructive' : 'text-muted-foreground'}`}>
          {stand.move.status === 'FAILED' ? 'Umzug unterbrochen' : 'Umzug läuft'}: {zahl(stand.move.movedObjects)} von{' '}
          {zahl(stand.move.totalObjects)} Dateien ({speicherText(stand.move.movedBytes)})
          {stand.move.lastError ? ` — ${stand.move.lastError}` : ''}
        </p>
      )}
      {stand.lastFailureAt && (
        <p className="mt-2 flex items-start gap-1.5 text-sm text-destructive">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          Letzte Störung am {datum(stand.lastFailureAt)}: {stand.lastFailureMessage}
        </p>
      )}

      {stand.provider === 's3' && (
        <div className="mt-4 border-t border-border pt-3">
          <p className="text-sm font-medium text-foreground">Rückweg auf den Symphenia-Speicher</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {stand.returnApproved
              ? 'Freigegeben. Der Mandant kann zurückwechseln, sofern sein Bestand ins Kontingent passt. Die Freigabe wird dabei verbraucht.'
              : 'Gesperrt. Der Mandant kann erst zurückwechseln, wenn du zustimmst — die Dateien ziehen dann auf die Platte von Symphenia um.'}
          </p>
          <button
            type="button"
            onClick={() => void schalte(!stand.returnApproved)}
            disabled={beschaeftigt || umzugOffen}
            className="mt-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50"
          >
            {stand.returnApproved ? 'Freigabe zurücknehmen' : 'Rückweg freigeben'}
          </button>
        </div>
      )}
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
    </div>
  )
}

// ─── Zielmenge: Paket, Fristen, Vorschau ──────────────────────────────────────

/**
 * Die Leiste, an der geschaltet wird.
 *
 * **Vorschau vor Wirkung**: `dryRun` rechnet denselben Vorgang, ohne etwas zu
 * schreiben. Das ist mehr als Bequemlichkeit — die Antwort nennt auch, was
 * **gekündigt** würde, und genau das sieht man einer Häkchenliste nicht an.
 */
function ZielLeiste({
  pakete,
  herkunft,
  setHerkunft,
  testEnde,
  setTestEnde,
  laufzeitEnde,
  setLaufzeitEnde,
  geaendert,
  beschaeftigt,
  vorschau,
  name,
  onPaket,
  onVorschau,
  onUebernehmen,
  onZuruecksetzen,
}: {
  pakete: ModulePackage[]
  herkunft: string
  setHerkunft: (wert: string) => void
  testEnde: string
  setTestEnde: (wert: string) => void
  laufzeitEnde: string
  setLaufzeitEnde: (wert: string) => void
  geaendert: boolean
  beschaeftigt: boolean
  vorschau: SetModulesResult | null
  name: (key: string) => string
  onPaket: (paket: ModulePackage) => void
  onVorschau: () => void
  onUebernehmen: () => void
  onZuruecksetzen: () => void
}) {
  const paket = pakete.find((p) => p.key === herkunft)

  return (
    <div className={formCard}>
      <div className="flex flex-wrap items-end gap-4">
        {pakete.length > 0 && (
          <div>
            <label className="block text-xs text-muted-foreground">Paket übernehmen</label>
            <div className="mt-1 flex items-center gap-2">
              <select
                value={herkunft}
                onChange={(e) => {
                  const gewaehlt = pakete.find((p) => p.key === e.target.value)
                  if (gewaehlt) onPaket(gewaehlt)
                  else setHerkunft('')
                }}
                className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
                aria-label="Paket übernehmen"
              >
                <option value="">— kein Paket —</option>
                {pakete.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name}
                  </option>
                ))}
              </select>
              <Link to="/admin/pakete" className="text-xs text-primary hover:underline">
                <Package size={13} className="mr-1 inline" />
                Katalog
              </Link>
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs text-muted-foreground" htmlFor="testEnde">
            Testphase bis
          </label>
          <input
            id="testEnde"
            type="date"
            value={testEnde}
            onChange={(e) => setTestEnde(e.target.value)}
            className="mt-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>

        <div>
          <label className="block text-xs text-muted-foreground" htmlFor="laufzeitEnde">
            Laufzeit bis
          </label>
          <input
            id="laufzeitEnde"
            type="date"
            value={laufzeitEnde}
            onChange={(e) => setLaufzeitEnde(e.target.value)}
            className="mt-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onZuruecksetzen}
            disabled={!geaendert || beschaeftigt}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-40"
          >
            Zurücksetzen
          </button>
          <button
            type="button"
            onClick={onVorschau}
            disabled={beschaeftigt}
            className="rounded-md border border-primary/30 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 disabled:opacity-40"
          >
            Vorschau
          </button>
          <button
            type="button"
            onClick={onUebernehmen}
            disabled={!geaendert || beschaeftigt}
            className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {beschaeftigt ? 'Wird übernommen…' : 'Übernehmen'}
          </button>
        </div>
      </div>

      <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
        <Info size={13} className="mt-0.5 flex-shrink-0" />
        <span>
          Angehakt ist, was der Betrieb <strong>danach</strong> haben soll. Was fehlt, wird gekündigt;
          Voraussetzungen nimmt der Server mit. Fristen gelten nur für <strong>neu entstehende</strong>{' '}
          Buchungen.
          {paket && ` · Paket „${paket.name}": ${paket.resolvedModuleKeys.map(name).join(', ')}`}
        </span>
      </p>

      {vorschau && (
        <div className="mt-3 space-y-1 rounded-md bg-muted/40 px-3 py-2 text-xs">
          {vorschau.add.length > 0 && (
            <p className="text-foreground">
              <strong>Neu:</strong> {vorschau.add.map(name).join(', ')}
            </p>
          )}
          {vorschau.revive.length > 0 && (
            <p className="text-foreground">
              <strong>Wiederbelebt:</strong> {vorschau.revive.map(name).join(', ')}
            </p>
          )}
          {vorschau.cancel.length > 0 && (
            <p className="text-destructive">
              <strong>Gekündigt:</strong> {vorschau.cancel.map(name).join(', ')}
            </p>
          )}
          {vorschau.blocked.length > 0 && (
            <p className="text-muted-foreground">
              <strong>Nicht möglich:</strong>{' '}
              {vorschau.blocked.map((b) => `${name(b.moduleKey)} — ${b.reason}`).join(' · ')}
            </p>
          )}
          {vorschau.add.length === 0 &&
            vorschau.revive.length === 0 &&
            vorschau.cancel.length === 0 && (
              <p className="text-muted-foreground">
                Nichts zu tun — {vorschau.keep.length} Bausteine laufen unverändert weiter.
              </p>
            )}
        </div>
      )}
    </div>
  )
}

// ─── Eine Modulgruppe ─────────────────────────────────────────────────────────

function GruppenKarte({
  gruppe,
  name,
  aktiveBenutzer,
  ziel,
  onUmschalten,
  beschaeftigt,
  onPlaetze,
  onStatus,
}: {
  gruppe: Gruppe
  name: (key: string) => string
  aktiveBenutzer: number
  ziel: Set<string>
  onUmschalten: (key: string) => void
  beschaeftigt: boolean
  onPlaetze: (payload: { seatMode?: SeatMode; seats?: number }) => void
  onStatus: (moduleKey: string, status: 'ACTIVE' | 'SUSPENDED') => void
}) {
  const gebucht = gruppe.teile.find((tl) => tl.booking)
  const platz = gebucht?.booking
  // Ein eigenstaendiges Modul ist seine eigene Gruppe. Dann steht sein Name in
  // der Ueberschrift — ihn in der einzigen Zeile darunter zu wiederholen, sagt
  // nichts Zweites, sondern laesst zwei gleiche Namen so aussehen, als seien es
  // zwei Dinge.
  const eigenstaendig = gruppe.teile.length === 1 && gruppe.teile[0]!.key === gruppe.key

  return (
    <div className={formCard}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{name(gruppe.key)}</h2>
          <p className="font-mono text-[11px] text-muted-foreground">{gruppe.key}</p>
        </div>

        {platz && (
          <PlatzBlock
            seatMode={platz.seatMode}
            seats={platz.seats}
            seatsUsed={platz.seatsUsed}
            seatsDormant={platz.seatsDormant}
            aktiveBenutzer={aktiveBenutzer}
            beschaeftigt={beschaeftigt}
            onPlaetze={onPlaetze}
          />
        )}
      </div>

      <div className="mt-4 space-y-1">
        {gruppe.teile.map((teil) => {
          const b = teil.booking
          return (
            <div
              key={teil.key}
              className="flex flex-wrap items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted/40"
            >
              <label className="flex min-w-[16rem] flex-1 cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={ziel.has(teil.key)}
                  onChange={() => onUmschalten(teil.key)}
                  className="h-4 w-4 rounded border-border"
                  aria-label={name(teil.key)}
                />
                {!eigenstaendig && (
                  <>
                    <span className="text-sm text-foreground">{name(teil.key)}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">{teil.key}</span>
                  </>
                )}
              </label>

              {b ? (
                <>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[b.status] ?? ''}`}>
                    {STATUS_LABEL[b.status] ?? b.status}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    seit {datum(b.bookedAt)}
                    {b.trialEndsAt && ` · Test bis ${datum(b.trialEndsAt)}`}
                    {b.validUntil && ` · Laufzeit bis ${datum(b.validUntil)}`}
                    {b.endsAt && ` · endet ${datum(b.endsAt)}`}
                  </span>
                  <span className="text-xs font-medium text-foreground">
                    {b.price ? preisText(b.price) : 'ohne Preis'}
                  </span>
                  {(b.status === 'ACTIVE' || b.status === 'TRIAL') && (
                    <button
                      type="button"
                      disabled={beschaeftigt}
                      onClick={() => onStatus(teil.key, 'SUSPENDED')}
                      className="rounded-md border border-border px-2 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-40"
                      title="Sperrt den Zugang sofort. Daten bleiben unangetastet."
                    >
                      Aussetzen
                    </button>
                  )}
                  {b.status === 'SUSPENDED' && (
                    <button
                      type="button"
                      disabled={beschaeftigt}
                      onClick={() => onStatus(teil.key, 'ACTIVE')}
                      className="rounded-md border border-primary/30 px-2 py-1 text-xs text-primary hover:bg-primary/10 disabled:opacity-40"
                    >
                      Freigeben
                    </button>
                  )}
                </>
              ) : (
                <span className="text-xs text-muted-foreground">nicht gebucht</span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Plätze: der Betreiber verkauft die Zahl ──────────────────────────────────

/**
 * Die Platzzahl einer Modulgruppe.
 *
 * **Verteilt wird hier nichts.** Der Betreiber verkauft, wie viele Menschen das
 * Modul benutzen dürfen; wer davon einen Platz bekommt, entscheidet der
 * Administrator des Mandanten (AP-29-H) — er kennt seinen Betrieb, der
 * Betreiber nicht.
 *
 * Die Umstellung von „alle Benutzer" auf eine Zahl ist der folgenreichste
 * Handgriff der Konsole, deshalb steht vorher da, wen sie trifft: Der Server
 * belegt die gekauften Plätze mit den Dienstältesten vor, alle darüber hinaus
 * verlieren den Zugang zu **allen** Bausteinen der Gruppe, bis der Mandant neu
 * verteilt.
 */
function PlatzBlock({
  seatMode,
  seats,
  seatsUsed,
  seatsDormant,
  aktiveBenutzer,
  beschaeftigt,
  onPlaetze,
}: {
  seatMode: SeatMode
  seats: number | null
  seatsUsed: number
  seatsDormant: number
  aktiveBenutzer: number
  beschaeftigt: boolean
  onPlaetze: (payload: { seatMode?: SeatMode; seats?: number }) => void
}) {
  const [modus, setModus] = useState<SeatMode>(seatMode)
  const [zahl, setZahl] = useState(seats ? String(seats) : String(aktiveBenutzer))

  useEffect(() => {
    setModus(seatMode)
    setZahl(seats ? String(seats) : String(aktiveBenutzer))
  }, [seatMode, seats, aktiveBenutzer])

  const wechselt = modus !== seatMode
  const zahlGeaendert = modus === 'ASSIGNED' && Number(zahl) !== (seats ?? 0)
  const uebrig = aktiveBenutzer - Number(zahl || 0)

  return (
    <div className="min-w-[18rem] space-y-2 rounded-md border border-border/60 bg-muted/20 p-3">
      <div className="flex items-center gap-2 text-xs">
        <select
          value={modus}
          onChange={(e) => setModus(e.target.value as SeatMode)}
          className="rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
          style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
          aria-label="Platzvergabe"
        >
          <option value="ALL_USERS">Alle Benutzer</option>
          <option value="ASSIGNED">Begrenzte Plätze</option>
        </select>

        {modus === 'ASSIGNED' && (
          <input
            type="number"
            min={1}
            value={zahl}
            onChange={(e) => setZahl(e.target.value)}
            className="w-16 rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
            aria-label="Platzzahl"
          />
        )}

        <button
          type="button"
          disabled={beschaeftigt || (!wechselt && !zahlGeaendert) || (modus === 'ASSIGNED' && !Number(zahl))}
          onClick={() =>
            onPlaetze(
              modus === 'ASSIGNED'
                ? { seatMode: 'ASSIGNED', seats: Number(zahl) }
                : { seatMode: 'ALL_USERS' },
            )
          }
          className="rounded-md border border-primary/30 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 disabled:opacity-40"
        >
          Plätze setzen
        </button>
      </div>

      <p className="text-xs text-muted-foreground">
        {seatMode === 'ALL_USERS' ? (
          <>Alle Benutzer ({aktiveBenutzer}) — keine Platzgrenze</>
        ) : (
          <>
            <span className="font-medium text-foreground">
              {seatsUsed} von {seats ?? '–'} Plätzen belegt
            </span>
            {seatsDormant > 0 && <> · {seatsDormant} ruhend</>}
          </>
        )}
      </p>

      {modus === 'ASSIGNED' && seatMode === 'ALL_USERS' && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-yellow-600 dark:text-yellow-500" />
          <span>
            Der Server belegt die Plätze mit den dienstältesten Benutzern vor.
            {uebrig > 0 && (
              <>
                {' '}
                <strong>{uebrig} von {aktiveBenutzer}</strong> verlieren damit den Zugang zu allen
                Bausteinen dieser Gruppe,
              </>
            )}{' '}
            bis der Mandant die Plätze selbst verteilt (Einstellungen → Module → Plätze).
          </span>
        </p>
      )}
    </div>
  )
}

// ─── Preise dieses Mandanten ──────────────────────────────────────────────────

type Entwurf = {
  amount: string
  basis: CatalogRow['basis']
  period: CatalogRow['period']
  note: string
  /** „gültig ab" als YYYY-MM-DD — die Form, die ein `<input type="date">` führt. */
  validFrom: string
}

/** Heute als YYYY-MM-DD; die Vorgabe für eine neue Zusage. */
function heuteIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function tagIso(wert: string): string {
  return wert.slice(0, 10)
}

/**
 * Was dieser Kunde zahlt — und was er zahlen würde.
 *
 * Die Trennung, um die es geht: Im Menü **Preise** stehen die **Listenpreise**
 * für den Vertrieb. Hier steht daneben, was mit *diesem* Mandanten vereinbart
 * ist. Die Vereinbarung gilt für alles, was er ab jetzt bucht — auch für das,
 * was er ein halbes Jahr später dazubucht.
 *
 * **Laufende Buchungen ändert sie nicht.** Das ist Absicht: Sonst erhöhte eine
 * Preispflege rückwirkend, was ein Bestandskunde zahlt. Wo beides auseinander
 * läuft, bietet die Zeile den ausdrücklichen Schritt an — „auf … umstellen".
 */
function PreisTabelle({
  preise,
  pakete,
  stand,
  name,
  beschaeftigt,
  onSetzen,
  onEntfernen,
  onBuchungspreis,
}: {
  preise: TenantPriceRow[]
  pakete: ModulePackage[]
  stand: OperatorTenantModules
  name: (key: string) => string
  beschaeftigt: string | null
  onSetzen: (
    itemKey: string,
    payload: {
      amount: number
      basis: CatalogRow['basis']
      period: CatalogRow['period']
      note?: string
      validFrom: string
    },
  ) => void
  onEntfernen: (itemKey: string, validFrom?: string) => void
  onBuchungspreis: (
    moduleKey: string,
    amount: number,
    basis: CatalogRow['basis'],
    period: CatalogRow['period'],
  ) => void
}) {
  const [entwuerfe, setEntwuerfe] = useState<Record<string, Entwurf>>({})

  const nachKey = useMemo(() => new Map(preise.map((p) => [p.itemKey, p])), [preise])
  const buchungen = useMemo(
    () => new Map<string, OperatorModuleRow>(stand.modules.filter((m) => m.booking).map((m) => [m.key, m])),
    [stand],
  )

  /**
   * Aufgeführt wird, was sich verkaufen lässt: jeder buchbare Baustein und jedes
   * Paket. Eine Gruppe steht nicht dabei — sie ist eine Überschrift, kein
   * Angebot.
   */
  const posten = useMemo(
    () => [
      ...MODULE_REGISTRY.filter((m) => isBookable(m.key)).map((m) => ({
        key: m.key as string,
        art: 'Baustein' as const,
      })),
      ...pakete.map((p) => ({ key: p.key, art: 'Paket' as const })),
    ],
    [pakete],
  )

  const entwurfVon = (itemKey: string): Entwurf => {
    if (entwuerfe[itemKey]) return entwuerfe[itemKey]!
    const zeile = nachKey.get(itemKey)
    const a = zeile?.agreed
    return {
      amount: a ? String(a.amount) : '',
      basis: a?.basis ?? zeile?.list.basis ?? 'PER_SEAT',
      period: a?.period ?? zeile?.list.period ?? 'MONTH',
      note: a?.note ?? '',
      // Die geltende Zusage steht mit ihrem eigenen Datum da; wer sie ändert,
      // ändert sie. Ein neues Datum legt daneben eine zweite an — genau so ist
      // ein befristeter Preis gemeint.
      validFrom: a ? tagIso(a.validFrom) : heuteIso(),
    }
  }

  const setzeEntwurf = (itemKey: string, teil: Partial<Entwurf>) =>
    setEntwuerfe((prev) => ({ ...prev, [itemKey]: { ...entwurfVon(itemKey), ...teil } }))

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <div className="border-b border-border px-4 py-3">
        <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <Euro size={16} className="text-primary" /> Vereinbarte Preise
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Der <Link to="/admin/preise" className="text-primary hover:underline">Katalog</Link> führt
          die Listenpreise für den Vertrieb. Was hier steht, gilt für <strong>diesen</strong>{' '}
          Mandanten — für alles, was er ab dem <strong>gültig ab</strong> bucht. Es gilt immer die
          jüngste Zusage, deren Datum erreicht ist; ein späteres Datum legt eine <em>geplante</em>
          daneben, ohne heute etwas zu ändern. Laufende Buchungen behalten ihren Preis; weicht er
          ab, steht der Schritt dazu in der Zeile.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-left">
            <tr>
              <th className="px-4 py-2 font-medium text-foreground">Posten</th>
              <th className="px-4 py-2 font-medium text-foreground">Liste</th>
              <th className="px-4 py-2 font-medium text-foreground">Vereinbart</th>
              <th className="px-4 py-2 font-medium text-foreground">gültig ab</th>
              <th className="px-4 py-2 font-medium text-foreground">Notiz</th>
              <th className="px-4 py-2 font-medium text-foreground">Läuft mit</th>
              <th className="w-24" />
            </tr>
          </thead>
          <tbody>
            {posten.map(({ key, art }) => {
              const zeile = nachKey.get(key)
              const entwurf = entwurfVon(key)
              const buchung = buchungen.get(key)
              const laufend = buchung?.booking?.price ?? null
              const vereinbart = zeile?.agreed ?? null
              // Alles außer der geltenden Zusage — abgelöste und geplante. Sie
              // stehen klein darunter, damit weder eine alte Zusage spurlos
              // verschwindet noch eine künftige Erhöhung aus dem Nichts kommt.
              const weitere = (zeile?.entries ?? []).filter(
                (z) => z.validFrom !== vereinbart?.validFrom,
              )
              const abweichend =
                vereinbart && laufend && Number(laufend.amount) !== Number(vereinbart.amount)
              const arbeitet = beschaeftigt === `preis:${key}`

              return (
                <tr key={key} className="border-b border-border last:border-0">
                  <td className="px-4 py-2">
                    <div className="text-foreground">{name(key)}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">
                      {key} · {art}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {zeile && zeile.list.amount !== null
                      ? preisText({
                          amount: zeile.list.amount,
                          currency: zeile.list.currency,
                          basis: zeile.list.basis,
                          period: zeile.list.period,
                        })
                      : '—'}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={entwurf.amount}
                        onChange={(e) => setzeEntwurf(key, { amount: e.target.value })}
                        placeholder="—"
                        aria-label={`Vereinbarter Preis ${name(key)}`}
                        className="w-20 rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
                      />
                      <select
                        value={entwurf.basis}
                        onChange={(e) =>
                          setzeEntwurf(key, { basis: e.target.value as CatalogRow['basis'] })
                        }
                        aria-label={`Bezug ${name(key)}`}
                        className="rounded border border-border bg-background px-1 py-1 text-xs text-foreground"
                        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
                      >
                        <option value="PER_SEAT">je Platz</option>
                        <option value="FLAT">pauschal</option>
                      </select>
                      <select
                        value={entwurf.period}
                        onChange={(e) =>
                          setzeEntwurf(key, { period: e.target.value as CatalogRow['period'] })
                        }
                        aria-label={`Periode ${name(key)}`}
                        className="rounded border border-border bg-background px-1 py-1 text-xs text-foreground"
                        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
                      >
                        <option value="MONTH">/ Monat</option>
                        <option value="YEAR">/ Jahr</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="date"
                      value={entwurf.validFrom}
                      onChange={(e) => setzeEntwurf(key, { validFrom: e.target.value })}
                      aria-label={`Gültig ab ${name(key)}`}
                      className="rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
                    />
                    {weitere.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {weitere.map((z) => {
                          const geplant = new Date(z.validFrom) > new Date()
                          return (
                            <div
                              key={z.validFrom}
                              className="flex items-center gap-1 text-[11px] text-muted-foreground"
                            >
                              <span>
                                {geplant ? 'geplant ab ' : 'bis '}
                                {new Date(z.validFrom).toLocaleDateString()}:{' '}
                                {betrag(z.amount, z.currency)}
                              </span>
                              <button
                                type="button"
                                disabled={arbeitet}
                                onClick={() => onEntfernen(key, tagIso(z.validFrom))}
                                className="text-muted-foreground hover:text-destructive disabled:opacity-40"
                                title="Diese Zusage entfernen"
                                aria-label={`Zusage vom ${tagIso(z.validFrom)} entfernen`}
                              >
                                <Trash2 size={11} />
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="text"
                      value={entwurf.note}
                      onChange={(e) => setzeEntwurf(key, { note: e.target.value })}
                      placeholder="Zusage, Angebot, Befristung"
                      aria-label={`Notiz ${name(key)}`}
                      className="w-48 rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
                    />
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {laufend ? (
                      <span className={abweichend ? 'text-yellow-700 dark:text-yellow-500' : 'text-muted-foreground'}>
                        {preisText(laufend)}
                      </span>
                    ) : buchung ? (
                      <span className="text-muted-foreground">ohne Preis</span>
                    ) : (
                      <span className="text-muted-foreground">nicht gebucht</span>
                    )}
                    {buchung && vereinbart && (abweichend || !laufend) && (
                      <button
                        type="button"
                        disabled={arbeitet}
                        onClick={() =>
                          onBuchungspreis(key, vereinbart.amount, vereinbart.basis, vereinbart.period)
                        }
                        className="ml-2 rounded border border-border px-1.5 py-0.5 text-[11px] text-foreground hover:bg-muted disabled:opacity-40"
                        title="Setzt den laufenden Preis dieser Buchung auf den vereinbarten"
                      >
                        auf {betrag(vereinbart.amount, vereinbart.currency)} umstellen
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={arbeitet || !entwurf.amount}
                        onClick={() =>
                          onSetzen(key, {
                            amount: Number(entwurf.amount),
                            basis: entwurf.basis,
                            period: entwurf.period,
                            validFrom: entwurf.validFrom,
                            ...(entwurf.note ? { note: entwurf.note } : {}),
                          })
                        }
                        className="rounded-md border border-primary/30 p-1 text-primary hover:bg-primary/10 disabled:opacity-40"
                        title="Vereinbarten Preis speichern"
                        aria-label={`Speichern ${name(key)}`}
                      >
                        <Check size={14} />
                      </button>
                      {vereinbart && (
                        <button
                          type="button"
                          disabled={arbeitet}
                          onClick={() => onEntfernen(key, tagIso(vereinbart.validFrom))}
                          className="rounded-md border border-border p-1 text-muted-foreground hover:bg-muted hover:text-destructive disabled:opacity-40"
                          title="Diese Zusage zurücknehmen — dann gilt die vorige oder die Liste"
                          aria-label={`Entfernen ${name(key)}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
